import { Constants } from './../../shared/constants';
import { FlashMessagesService } from './../../shared/flash-messages/service/flash.messages.service';
import { Injectable } from '@angular/core';
import { Payment } from '../models/payment';
import { Observable } from 'rxjs/Observable';
import { Headers, RequestOptions, Response } from '@angular/http';
import { List, Map as ImmutableMap } from 'immutable';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { ResponseUtilService } from '../../shared/utils/response-util.service';
import { AppAuthHttpService } from '../../http/app.auth.http.service';
import { BulkActionResponse } from '../models/bulk-action-response';
import { PaymentSummary } from '../models/payment-summary/payment-summary';
import { AdjustmentType } from '../models/adjustment-type';
import { AdjustmentSummary } from '../models/payment-summary/adjustment-summary';
import { RoomCommissionSummary } from '../models/payment-summary/room-commission-summary';
import { PrepaymentSummary } from '../models/payment-summary/prepayment-summary';


interface RawPaymentSummaryResponse {
  [eventPartyId: number]: any;
}

@Injectable()
export class PaymentHttpService {

  private readonly eventsUrl: string = this.environment.baseUrl + '/api/events';
  private readonly paymentUrl: string = this.eventsUrl + '/payments';
  private readonly paymentBulkUrl: string = this.eventsUrl + '/payments/bulk';
  private readonly cancelUrl: string = this.eventsUrl + '/payments/cancel';
  private readonly paymentRequestUrl = this.paymentUrl + '/request';
  private readonly paymentSummaryUrl = this.paymentUrl + '/summary';

  constructor(private http: AppAuthHttpService,
              private environment: EnvironmentConfiguration,
              private responseUtilService: ResponseUtilService,
              private flashMessagesService: FlashMessagesService) {
  }

  add(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.paymentUrl, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  update(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.put(this.paymentUrl, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  bulkPayments(payments: Payment[]): Observable<List<BulkActionResponse> | Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.paymentBulkUrl, JSON.stringify(payments), options)
      .map((res: Response) => res.json())
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  cancelNew(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.cancelUrl, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  cancelExisting(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.put(this.cancelUrl, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  getPayments(eventId: number): Observable<List<Payment> | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/payments`)
      .map(response => List<Payment>(response.json().map(element => new Payment(element))))
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getPaymentsByParty(eventId: number, eventPartyId: number): Observable<List<Payment> | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/${eventPartyId}/payments`)
      .map(response => List<Payment>(response.json().map(element => new Payment(element))))
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getLatestFinalPayment(eventId: number): Observable<Payment | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/latestFinalPayment`)
      .map(response => {
        return this.mapPaymentResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getLatestVendorFinalPayment(eventId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/latestVendorFinalPayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getLatestVendorFinalPaymentByParty(eventId: number, eventPartyId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/${eventPartyId}/latestVendorFinalPayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getLatestPrepayment(eventId: number): Observable<Payment | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/latestPrepayment`)
      .map(response => {
        return this.mapPaymentResponse(response);
      })
      .catch(errorResponse => this.responseUtilService.handleError(errorResponse))
      .share();
  }

  getLatestVendorPrepayments(eventId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/latestVendorPrepayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(errorResponse => this.responseUtilService.handleError(errorResponse))
      .share();
  }

  getLatestVendorPrepaymentsByParty(eventId: number, eventPartyId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/${eventPartyId}/latestVendorPrepayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(errorResponse => this.responseUtilService.handleError(errorResponse))
      .share();
  }

  getFinalPaymentStatusList(eventId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/finalPaymentStatusList`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getFinalPaymentStatusListByParty(eventId: number, eventPartyId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/${eventPartyId}/finalPaymentStatusList`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  resendPaymentRequest(eventId: number): Observable<Response> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.put(`${this.eventsUrl}/${eventId}/resendPaymentRequest`, null, options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  getLatestInitialPaymentsByParty(eventId: number, eventPartyId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/${eventPartyId}/initialPayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  getLatestInitialPayments(eventId: number): Observable<Payment[] | Response> {
    return this.http.get(`${this.eventsUrl}/${eventId}/initialPayments`)
      .map(response => {
        return this.mapPaymentArrayResponse(response);
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  updateForecastedCommissionAmount(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.put(`${this.paymentUrl}/forecasted/commission`, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  withdrawPaymentRequest(eventId: number, eventPartyId: number) {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.put(`${this.eventsUrl}/${eventId}/${eventPartyId}/withdrawPayment`, null, options)
        .catch(response => {
          return this.handleError(response);
        }).share();
  }

  private handleError(response: Response): Observable<Response> {
    let result: Observable<Response>;
    const responseObject = (<Response>response).json();
    if (responseObject && (<string>responseObject.error).indexOf(Constants.AGENCY_SERVICE_ERROR_RESPONSE) !== -1) {
      this.flashMessagesService.error('MESSAGES.ERROR.AGENCY_SERVICE_ADD_REQUEST');
      result = Observable.empty();
    } else {
      result = this.responseUtilService.handleError(response);
    }
    return result;
  }

  /**
   * FIXME The status property is declared as PaymentStatus in the Payment model
   * but the string returned from the API is not converted to PaymentStatus,
   * so it is a string at runtime and comparing status and PaymentStatus with ===
   * is not working.
   */
  private mapPaymentResponse(response: Response) {
    let payment: Payment = null;
    const jsonPayment = response.json();
    if (jsonPayment) {
      payment = new Payment(jsonPayment);
    }
    return payment;
  }

  /**
   * FIXME The status property is declared as PaymentStatus in the Payment model
   * but the string returned from the API is not converted to PaymentStatus,
   * so it is a string at runtime and comparing status and PaymentStatus with ===
   * is not working.
   */
  private mapPaymentArrayResponse(response: Response) {
    return response.json() as Array<Payment>;
  }

  request(payment: Payment): Observable<any> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return this.http.post(this.paymentRequestUrl, JSON.stringify(payment.toJS()), options)
      .catch(response => {
        return this.handleError(response);
      }).share();
  }

  fetchPaymentSummary(eventPartyIds: Set<number>): Observable<ImmutableMap<number, PaymentSummary>> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });

    return this.http.post(this.paymentSummaryUrl, JSON.stringify(Array.from(eventPartyIds)), options)
      .map((response: Response) => {
        const json: RawPaymentSummaryResponse = response.json();

        const keys = Object.keys(json);
        if (!keys.length) {
          throw new Error('Payment summary response is empty or missing eventPartyIds');
        }

        return ImmutableMap<number, PaymentSummary>(
          keys.map(Number).map((eventPartyId: number) => [
            eventPartyId,
            this.deserializePaymentSummary(json[eventPartyId])
          ])
        );
      })
      .share();
  }

  private deserializePaymentSummary(json: any): PaymentSummary {
    return new PaymentSummary({
      eventPartyId: json.eventPartyId,
      roomCommissionSummary: this.deserializeRoomCommissionSummary(json.roomCommissionSummary),
      adjustmentSummaryList: List<AdjustmentSummary>((json.adjustmentSummaryList || []).map(this.deserializeAdjustmentSummary)),
      modifierSummaryList: List<AdjustmentSummary>((json.modifierSummaryList || []).map(this.deserializeAdjustmentSummary)),
      prepayment: this.deserializePrepaymentSummary(json.prepayment),
      estimatedTax: json.estimatedTax,
      calculatedPayment: json.calculatedPayment,
      paymentStatus: json.paymentStatus,
      commissionAmount: json.commissionAmount,
    });
  }

  private deserializeRoomCommissionSummary(json: any): RoomCommissionSummary {
    return new RoomCommissionSummary({
      totalSpend: json.totalSpend,
      commRate: json.commRate,
      amountDue: json.amountDue,
      roomCommissionType: json.roomCommissionType,
      shoulderDatesExcluded: json.shoulderDatesExcluded,
      totalNights: json.totalNights,
      avgRoomRate: json.avgRoomRate
    });
  }

  private deserializePrepaymentSummary(json: any): PrepaymentSummary {
    if (!json) {
      return null;
    }
    return new PrepaymentSummary({
      amount: json.amount,
      description: json.description
    });
  }

  private deserializeAdjustmentSummary = (json: any): AdjustmentSummary => {
    return new AdjustmentSummary({
      eventAdjustmentId: json.eventAdjustmentId,
      totalSpend: json.totalSpend,
      commRate: json.commRate,
      amountDue: json.amountDue,
      adjustmentType: this.deserializeAdjustmentType(json.adjustmentType),
      description: json.description
    });
  }

  private deserializeAdjustmentType(json: any): AdjustmentType {
    return new AdjustmentType({
      id: json.id,
      name: json.name,
      description: json.description,
      credit: json.credit,
      debit: json.debit
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/payment.http.service.ts